// EOL/EOS Product Relations Modal - Manage product ID mappings to external systems

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  Stack,
  TextField,
  DetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  IconButton,
  MessageBar,
  MessageBarType,
  Text,
  Spinner,
  SpinnerSize,
  useTheme,
} from '@fluentui/react';
import type { IColumn } from '@fluentui/react';
import { useEolEos } from '../contexts';
import type { EndOfLifeProduct, ProductRelation } from '../types';

interface ProductRelationsModalProps {
  product: EndOfLifeProduct | null;
  isOpen: boolean;
  onDismiss: () => void;
}

export const ProductRelationsModal: React.FC<ProductRelationsModalProps> = ({
  product,
  isOpen,
  onDismiss,
}) => {
  const theme = useTheme();
  const { getProductRelations, addProductRelation, deleteProductRelation, canWrite } = useEolEos();

  const [relations, setRelations] = useState<ProductRelation[]>([]);
  const [newRelation, setNewRelation] = useState({ mspVendor: '', mspProdId: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Load relations when dialog opens
  useEffect(() => {
    if (isOpen && product?.id) {
      loadRelations();
    }
  }, [isOpen, product?.id]);

  // Reset form state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setNewRelation({ mspVendor: '', mspProdId: '' });
      setErrorMessage('');
      setSuccessMessage('');
      setRelations([]);
    }
  }, [isOpen]);

  const loadRelations = async () => {
    if (!product?.id) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const result = await getProductRelations(product.id);
      if (result) {
        setRelations(result);
      }
    } catch (err: any) {
      setErrorMessage('Failed to load relations: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRelation = async () => {
    if (!product?.id) return;

    if (!newRelation.mspVendor.trim() || !newRelation.mspProdId.trim()) {
      setErrorMessage('Both MSP Vendor and MSP Product ID are required');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const result = await addProductRelation({
        productId: product.id,
        mspVendor: newRelation.mspVendor.trim(),
        mspProdId: newRelation.mspProdId.trim(),
      });

      if (result) {
        setNewRelation({ mspVendor: '', mspProdId: '' });
        setSuccessMessage('Mapping added successfully');
        await loadRelations();
      }
    } catch (err: any) {
      setErrorMessage('Failed to add mapping: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRelation = async (relation: ProductRelation) => {
    if (!product?.id) return;

    setDeletingId(relation.id);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const success = await deleteProductRelation(
        product.id,
        relation.mspVendor,
        relation.mspProdId
      );

      if (success) {
        setSuccessMessage('Mapping deleted successfully');
        await loadRelations();
      }
    } catch (err: any) {
      setErrorMessage('Failed to delete mapping: ' + (err.message || 'Unknown error'));
    } finally {
      setDeletingId(null);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'mspVendor',
      name: 'MSP Vendor',
      fieldName: 'mspVendor',
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'mspProdId',
      name: 'MSP Product ID',
      fieldName: 'mspProdId',
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 60,
      maxWidth: 60,
      onRender: (item: ProductRelation) =>
        canWrite ? (
          <IconButton
            iconProps={{ iconName: 'Delete' }}
            title="Delete mapping"
            ariaLabel="Delete mapping"
            onClick={() => handleDeleteRelation(item)}
            disabled={deletingId === item.id}
            styles={{
              root: { color: theme.palette.red },
              rootHovered: { color: theme.palette.redDark },
            }}
          />
        ) : null,
    },
  ];

  const dialogContentProps = {
    type: DialogType.normal,
    title: product ? `Product ID Mappings - ${product.productName}` : 'Product ID Mappings',
    subText: 'Map this product to external system identifiers (MSP vendor systems).',
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={onDismiss}
      dialogContentProps={dialogContentProps}
      minWidth={600}
      maxWidth={700}
      modalProps={{
        isBlocking: false,
        styles: { main: { maxWidth: 700 } },
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        {errorMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setErrorMessage('')}
          >
            {errorMessage}
          </MessageBar>
        )}

        {successMessage && (
          <MessageBar
            messageBarType={MessageBarType.success}
            onDismiss={() => setSuccessMessage('')}
          >
            {successMessage}
          </MessageBar>
        )}

        {canWrite && (
          <Stack tokens={{ childrenGap: 12 }}>
            <Text variant="mediumPlus">Add New Mapping</Text>
            <Stack horizontal tokens={{ childrenGap: 10 }} wrap>
              <Stack.Item grow={1} styles={{ root: { minWidth: 200 } }}>
                <TextField
                  label="MSP Vendor"
                  placeholder="e.g., ConnectWise"
                  value={newRelation.mspVendor}
                  onChange={(_, value) =>
                    setNewRelation((prev) => ({ ...prev, mspVendor: value || '' }))
                  }
                  required
                  disabled={isLoading}
                />
              </Stack.Item>
              <Stack.Item grow={1} styles={{ root: { minWidth: 200 } }}>
                <TextField
                  label="MSP Product ID"
                  placeholder="e.g., PROD-12345"
                  value={newRelation.mspProdId}
                  onChange={(_, value) =>
                    setNewRelation((prev) => ({ ...prev, mspProdId: value || '' }))
                  }
                  required
                  disabled={isLoading}
                />
              </Stack.Item>
              <Stack.Item styles={{ root: { alignSelf: 'flex-end' } }}>
                <PrimaryButton
                  text="Add"
                  iconProps={{ iconName: 'Add' }}
                  onClick={handleAddRelation}
                  disabled={
                    isLoading ||
                    !newRelation.mspVendor.trim() ||
                    !newRelation.mspProdId.trim()
                  }
                />
              </Stack.Item>
            </Stack>
          </Stack>
        )}

        <Stack tokens={{ childrenGap: 8 }}>
          <Text variant="mediumPlus">Existing Mappings</Text>

          {isLoading && !deletingId ? (
            <Stack horizontalAlign="center" styles={{ root: { padding: 20 } }}>
              <Spinner size={SpinnerSize.medium} label="Loading mappings..." />
            </Stack>
          ) : relations.length === 0 ? (
            <Stack
              horizontalAlign="center"
              styles={{
                root: {
                  padding: 20,
                  backgroundColor: theme.palette.neutralLighterAlt,
                  borderRadius: 4,
                },
              }}
            >
              <Text style={{ color: theme.palette.neutralSecondary }}>
                No mappings found for this product.
              </Text>
            </Stack>
          ) : (
            <div
              style={{
                border: `1px solid ${theme.palette.neutralLight}`,
                borderRadius: 4,
              }}
            >
              <DetailsList
                items={relations}
                columns={columns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.justified}
                compact={true}
              />
            </div>
          )}
        </Stack>
      </Stack>

      <DialogFooter>
        <PrimaryButton onClick={onDismiss} text="Close" />
      </DialogFooter>
    </Dialog>
  );
};
